/**
 * Local Storage Service
 *
 * This module provides utilities for managing data in browser localStorage.
 * Used primarily for persisting authentication state and user preferences.
 *
 * Storage Structure:
 * localStorage:
 *   - 'spa-datastage-ui': JSON object containing app config
 *     - 'userId': Current user's ID
 *     - 'access-token': JWT authentication token
 *     - 'roles': User's role/permissions
 *   - 'token': Duplicate token storage (legacy)
 *   - 'postLoginRedirect': Path to redirect after login
 *   - 'activePanel': Currently active navigation panel
 *
 * Usage:
 * ```
 * import { getAuthToken, getCurrentUser, setCurrentUser } from 'datastage/utils/localStorageService';
 *
 * const token = getAuthToken();
 * const user = getCurrentUser();
 * setCurrentUser(userId);
 * ```
 *
 * @see src/datastage/config/constants.js - LOCAL_STORAGE_KEYS definitions
 */
import { LOCAL_STORAGE_KEYS } from '../config/constants';

/**
 * Local Config Manager
 * Provides fetch/save operations for the main app config object
 */
export const localConfig = () => {
    /**
     * Fetches the current config from localStorage
     * @returns {Object} The stored config with a save method attached
     */
    function fetch() {
        let localConfig = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.main) || '{}');
        localConfig.save = save;
        return localConfig;
    }

    /**
     * Saves a value to the config in localStorage
     * @param {string} keyPath - The key to save under
     * @param {*} newValue - The value to save
     */
    function save(keyPath, newValue) {
        const config = localConfig().fetch();
        let newCfg = { ...config, [keyPath]: newValue };
        localStorage.setItem(LOCAL_STORAGE_KEYS.main, JSON.stringify(newCfg));
    }

    return {
        fetch: fetch,
    };
};

/**
 * Sets the auth token in sessionStorage
 * @param {string} token - JWT token
 */
export function setAuthToken(token) {
    try {
        sessionStorage.setItem('authToken', token);
    } catch (error) {
        console.error('Error setting auth token in session storage:', error);
    }
}

/**
 * Retrieves the auth token from localStorage
 * @returns {string|null} The stored token or null
 */
export function getAuthToken() {
    try {
        return localStorage.getItem('token');
    } catch (error) {
        console.error('Error getting auth token from session storage:', error);
        return null;
    }
}

/**
 * Clears the auth token from localStorage
 */
export function clearAuthToken() {
    try {
        localStorage.setItem('token', '');
    } catch (error) {
        console.error('Error setting auth token in session storage:', error);
    }
}

/**
 * Gets the token from the app config
 * @returns {string|undefined} The stored token
 */
export const getToken = () => {
    const config = localConfig().fetch();
    return config[LOCAL_STORAGE_KEYS.token];
};

/**
 * Gets the current user ID from the app config
 * @returns {string|undefined} The current user's ID
 */
export const getCurrentUser = () => {
    const config = localConfig().fetch();
    return config[LOCAL_STORAGE_KEYS.activeUser];
};

/**
 * Stores the current user ID in the app config
 * @param {string} userId - The user ID to store
 */
export const setCurrentUser = (userId) => {
    const config = localConfig().fetch();
    config.save([LOCAL_STORAGE_KEYS.activeUser], userId);
};

/**
 * Stores the current user's role in the app config
 * @param {string} userRole - The user role to store
 */
export const setCurrentUserRole = (userRole) => {
    const config = localConfig().fetch();
    config.save([LOCAL_STORAGE_KEYS.activeUserRole], userRole);
};

/**
 * Gets the current user's role from the app config
 * @returns {string|undefined} The current user's role
 */
export const getCurrentUserRole = () => {
    const config = localConfig().fetch();
    return config[LOCAL_STORAGE_KEYS.activeUserRole];
};

/**
 * Clears the active user's token (used on logout)
 */
export const resetActiveUser = () => {
    const config = localConfig().fetch();
    config.save([LOCAL_STORAGE_KEYS.token], undefined);
};
